import React from 'react';
import { HINWEISKASTEN, KNOPF_SEKUNDAER } from '../public/teilnahmeSeiteStil';
import { useKundenportal } from './useKundenportal';
import { PortalRahmen } from './PortalRahmen';
import { PinAnsicht } from './PinAnsicht';
import { UebersichtTab } from './UebersichtTab';
import { MitarbeiterTab } from './MitarbeiterTab';
import { UnterweisungenTab } from './UnterweisungenTab';
import { DokumenteTab } from './DokumenteTab';
import { FristenTab } from './FristenTab';

/**
 * Oeffentliche Kundenseite #/kundenportal/<token>.
 *
 * Wird OHNE Login gerendert (index.tsx routet am Auth-Gate vorbei, wie
 * #/unterweisung). Duenner Container: der Zustand lebt in useKundenportal,
 * die Regeln in kundenportalSeiteLogic und services/sifa/kundenportalLogic;
 * hier wird nur der Zustand auf die passende Ansicht abgebildet.
 *
 * Der Kunde sieht: Betriebsdaten und Ansprechpartner, seinen
 * Mitarbeiterstamm (pflegbar), Unterweisungen und Teilnahmen, Dokumente,
 * offene Fristen. Er sieht NICHT: Taetigkeitsnachweis, Abrechnung,
 * Begehungsberichte, Unterschriften, Antworten — die RPC liefert sie nicht.
 */
export const KundenportalPage: React.FC<{ token: string }> = ({ token }) => {
  const k = useKundenportal(token);

  if (k.status === 'ungueltig') {
    return (
      <PortalRahmen>
        <h2 className="text-lg font-bold text-slate-900">Dieser Zugang ist nicht verfügbar</h2>
        <div className={HINWEISKASTEN}>{k.meldung || 'Dieser Link ist nicht gültig.'}</div>
        <p className="text-sm text-slate-600">Bitte wenden Sie sich an Ihre Fachkraft für Arbeitssicherheit — sie kann den Zugang neu freischalten.</p>
      </PortalRahmen>
    );
  }

  if (k.status === 'pin' || (k.status === 'laedt' && !k.daten)) {
    return (
      <PortalRahmen>
        <PinAnsicht busy={k.status === 'laedt' || k.busy} meldung={k.meldung} onAnmelden={(pin) => void k.anmelden(pin)} />
      </PortalRahmen>
    );
  }

  if (k.status === 'fehler' && !k.daten) {
    return (
      <PortalRahmen onAbmelden={k.abmelden}>
        <h2 className="text-lg font-bold text-slate-900">Der Kundenbereich konnte nicht geladen werden</h2>
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{k.meldung}</div>
        <button type="button" onClick={() => void k.neuLaden()} className={KNOPF_SEKUNDAER}>Erneut versuchen</button>
      </PortalRahmen>
    );
  }

  const daten = k.daten;
  if (!daten) return null;

  let inhalt: React.ReactNode;
  switch (k.tab) {
    case 'mitarbeiter':
      inhalt = <MitarbeiterTab daten={daten} onSpeichern={k.mitarbeiterSpeichern} />;
      break;
    case 'unterweisungen':
      inhalt = <UnterweisungenTab daten={daten} dateiBusyId={k.dateiBusyId} onDatei={(art, id) => void k.dateiHerunterladen(art, id)} />;
      break;
    case 'dokumente':
      inhalt = <DokumenteTab daten={daten} dateiBusyId={k.dateiBusyId} onDatei={(art, id) => void k.dateiHerunterladen(art, id)} />;
      break;
    case 'fristen':
      inhalt = <FristenTab daten={daten} />;
      break;
    case 'uebersicht':
    default:
      inhalt = <UebersichtTab daten={daten} onTab={k.setTab} />;
  }

  return (
    <PortalRahmen
      bueroName={daten.bueroName}
      betriebName={daten.betrieb.name}
      tab={k.tab}
      onTab={k.setTab}
      meldung={k.meldung}
      onMeldungSchliessen={k.meldungSchliessen}
      onNeuLaden={() => void k.neuLaden()}
      onAbmelden={k.abmelden}
      busy={k.busy}
    >
      {inhalt}
    </PortalRahmen>
  );
};
